import { OnInit, Input, Directive, ElementRef, Optional, Self } from '@angular/core';
import { BllFormFieldControl } from '../form-field/form-field-control';
import { Subject } from 'rxjs';
import { NgControl, FormGroupDirective, NgForm, FormControl } from '@angular/forms';
import { ShowOnDirtyErrorStateMatcher } from '../utils/error-options';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

let nextUniqueId = 0;

// Invalid input type. Using one of these will throw an MatInputUnsupportedTypeError.
const UI_INPUT_INVALID_TYPES = ['button', 'checkbox', 'file', 'hidden', 'image', 'radio', 'range', 'reset', 'submit'];
const neverEmptyInputTypes = ['date', 'datetime', 'datetime-local', 'month', 'time', 'week'];

@Directive({
  selector: `[bllInput]`,
  exportAs: 'bllInput',
  host: {
    class: 'bll-input',
    '[attr.id]': 'id',
    '[attr.placeholder]': 'placeholder',
    // '[attr.disabled]': 'disabled',
    '[required]': 'required',
    '[attr.readonly]': 'readonly || null',
    '[attr.aria-invalid]': 'errorState',
    '[attr.aria-required]': 'required.toString()',
    '(paste)': '_paste($event)',
    '(blur)': '_focusChanged(false)',
    '(focus)': '_focusChanged(true)',
    '(input)': '_onInput()',
  },
  providers: [{ provide: BllFormFieldControl, useExisting: bllInput }, ShowOnDirtyErrorStateMatcher],
})
export class bllInput implements BllFormFieldControl<any>, OnInit {
  protected _uid = `bll-input-${nextUniqueId++}`;

  focused: boolean = false;
  readonly stateChanges: Subject<void> = new Subject<void>();
  controlType: string = 'bll-input';
  _parentFormGroup: FormGroupDirective;
  _defaultErrorStateMatcher: ShowOnDirtyErrorStateMatcher;
  _parentForm: FormGroupDirective;
  protected _previousNativeValue: any;

  @Input()
  get disabled(): any {
    if (this.ngControl && this.ngControl.disabled !== null) {
      return this.ngControl.disabled;
    }
    return this._disabled;
  }

  set disabled(value: any) {
    this._disabled = coerceBooleanProperty(value);
    // if(value){
    //   this.ngControl['disable'];
    // }

    // Browsers may not fire the blur event if the input is disabled too quickly.
    // Reset from here to ensure that the element doesn't become stuck.
    if (this.focused) {
      this.focused = false;
      this.stateChanges.next();
    }
  }
  protected _disabled = false;

  @Input()
  get id(): string {
    return this._id;
  }
  set id(value: string) {
    this._id = value || this._uid;
  }
  protected _id: string;

  @Input() placeholder: string;

  @Input()
  get required(): any {
    return this._required;
  }
  set required(value: any) {
    this._required = coerceBooleanProperty(value);
  }
  protected _required = false;

  /** Input type of the element. */
  @Input()
  get type(): string {
    return this._type;
  }
  set type(value: string) {
    this._type = value || 'text';
    this._validateType();

    // When using Angular inputs, developers are no longer able to set the properties on the native
    // input element. To ensure that bindings for `type` work, we need to sync the setter
    // with the native property. Textarea elements don't support the type property or attribute.
    if (!this._isTextarea()) {
      (this._elementRef.nativeElement as HTMLInputElement).type = this._type;
    }
  }
  protected _type = 'text';

  /** An object used to control when error messages are shown. */
  @Input() errorStateMatcher: ShowOnDirtyErrorStateMatcher;
  @Input() error: boolean;

  /** Whether the element is readonly. */
  @Input()
  get readonly(): any {
    return this._readonly;
  }
  set readonly(value: any) {
    this._readonly = coerceBooleanProperty(value);
  }
  private _readonly = false;

  get empty(): boolean {
    return !this._isNeverEmpty() && !this._elementRef.nativeElement.value && !this._isBadInput();
  }
  /** Whether the component is in an error state. */
  errorState: boolean = false;

  updateErrorState() {
    const oldState = this.errorState;
    const parent = this._parentFormGroup || this._parentForm;
    const matcher = this.errorStateMatcher || this._defaultErrorStateMatcher;
    const control = this.ngControl ? (this.ngControl.control as FormControl) : null;
    const newState = matcher.isErrorState(control, parent);

    if (newState !== oldState) {
      this.errorState = newState;
      this.stateChanges.next();
    }
  }
  constructor(
    protected _elementRef: ElementRef,
    @Optional() _parentForm: NgForm,
    @Optional() _parentFormGroup: FormGroupDirective,
    _defaultErrorStateMatcher: ShowOnDirtyErrorStateMatcher,
    @Optional() @Self() public ngControl: NgControl
  ) {
    // const element = this._elementRef.nativeElement;
    this._parentFormGroup = _parentFormGroup;
    this._parentForm = _parentFormGroup;
    this._defaultErrorStateMatcher = _defaultErrorStateMatcher;

    // Force setter to be called in case id was not specified.
    this.id = this.id;
  }

  ngOnInit() {}

  /** no paste. */
  @Input()
  get noPaste(): any {
    return this._noPaste;
  }
  set noPaste(value: any) {
    this._noPaste = coerceBooleanProperty(value);
  }
  private _noPaste = false;
  _paste($event) {
    if (this._noPaste) {
      $event.preventDefault();
    }
  }

  /** Make sure the input is a supported type. */
  protected _validateType() {
    if (UI_INPUT_INVALID_TYPES.indexOf(this._type) > -1) {
      throw Error(`Input type "${this.type}" isn't supported by bllInput.`);
    }
  }

  /** Determines if the component host is a textarea. */
  protected _isTextarea() {
    return this._elementRef.nativeElement.nodeName.toLowerCase() === 'textarea';
  }

  /** Checks whether the input type is one of the types that are never empty. */
  protected _isNeverEmpty() {
    return neverEmptyInputTypes.indexOf(this._type) > -1;
  }

  /** Checks whether the input is invalid based on the native validation. */
  protected _isBadInput() {
    // The `validity` property won't be present on platform-server.
    let validity = (this._elementRef.nativeElement as HTMLInputElement).validity;
    return validity && validity.badInput;
  }
  /** Focuses the input. */
  focus(): void {
    this._elementRef.nativeElement.focus();
  }
  /** Callback for the cases where the focused state of the input changes. */
  _focusChanged(isFocused: boolean) {
    if (isFocused !== this.focused && (!this.readonly || !isFocused)) {
      this.focused = isFocused;
      this.stateChanges.next();
    }
  }

  onContainerClick() {
    if (!this.focused) {
      this.focus();
    }
  }
  _onInput() {
    // This is a noop function and is used to let Angular know whenever the value changes.
    // Angular will run a new change detection each time the `input` event has been dispatched.
    // It's necessary that Angular recognizes the value change, because when floatingLabel
    // is set to false and Angular forms aren't used, the placeholder won't recognize the
    // value changes and will not disappear.
    // Listening to the input event wouldn't be necessary when the input is using the
    // FormsModule or ReactiveFormsModule, because Angular forms also listens to input events.
  }
  /** Does some manual dirty checking on the native input `value` property. */
  protected _dirtyCheckNativeValue() {
    const newValue = this._elementRef.nativeElement.value;

    if (this._previousNativeValue !== newValue) {
      this._previousNativeValue = newValue;
      this.stateChanges.next();
    }
  }

  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }
  ngDoCheck() {
    if (this.ngControl) {
      // We need to re-evaluate this on every change detection cycle, because there are some
      // error triggers that we can't subscribe to (e.g. parent form submissions). This means
      // that whatever logic is in here has to be super lean or we risk destroying the performance.
      this.updateErrorState();
    }

    // We need to dirty-check the native element's value, because there are some cases where
    // we won't be notified when it changes (e.g. the consumer isn't using forms or they're
    // updating the value using `emitEvent: false`).
    this._dirtyCheckNativeValue();
  }
}
